/**
 * RowActionIcons. Shared grid-template-standard icon buttons for the compact
 * action group on a grid row (and the matching actions on a detail page).
 *
 * These are SHARED components (COCKPIT.md section 2.10). They standardize the
 * look (muted ink, hover to ink, h-4 w-4 lucide glyph) and the safe click
 * handling (stopPropagation + preventDefault so a row-level click handler or a
 * wrapping <Link> does not also fire). The Finance Invoices grid + detail are
 * the first consumers; Commerce Orders adopts them under issue #218.
 *
 *   - RowIconButton   the base button (shared chrome + safe click).
 *   - OpenDocumentButton  opens a document (e.g. the invoice PDF) in a new tab.
 *   - DismissRecordButton a destructive "this is not what the tool tracks"
 *                         action (e.g. "Not an invoice") behind a confirm.
 *   - NotesIconButton a notes icon with a has-notes count badge that opens the
 *                     shared centered <NotesModal> (the icon affordance; the
 *                     modal renders separately as an overlay).
 */

import {
  ExternalLink,
  Ban,
  MessageSquare,
  MessageSquareText,
  type LucideIcon,
} from 'lucide-react';

const ICON_BTN_CLS =
  'inline-flex items-center text-amp-ink-muted hover:text-amp-ink ' +
  'dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The base row-action icon button. Renders a lucide icon, applies the shared
 * chrome, and (by default) stops the click from bubbling to a row click handler
 * or wrapping link. Pass `tone` to override the default muted-ink color (e.g. a
 * red destructive tone).
 */
export function RowIconButton({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: string;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!disabled) onClick();
      }}
      className={tone ? `inline-flex items-center ${tone}` : ICON_BTN_CLS}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

/**
 * Opens a document URL in a new browser tab. Used to replace a textual "source"
 * field/column with a compact icon (e.g. open the invoice PDF). Uses
 * `noopener` so the new tab cannot reach back into this window.
 */
export function OpenDocumentButton({
  url,
  label = 'Open PDF',
  disabled = false,
}: {
  url: string;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <RowIconButton
      icon={ExternalLink}
      label={label}
      disabled={disabled}
      onClick={() => window.open(url, '_blank', 'noopener')}
    />
  );
}

/**
 * The notes affordance for a grid row and a detail header: a notes icon that
 * opens the shared centered <NotesModal>. When the record has notes
 * (`count > 0`) the icon renders filled (MessageSquareText) with a small count
 * badge; otherwise it is the outline glyph. Clicking it calls `onOpen` so the
 * host can open the modal (and, if it loads notes lazily, enable that query).
 * Click handling stops propagation so a row click handler or wrapping <Link>
 * does not also fire.
 */
export function NotesIconButton({
  count = 0,
  label = 'Notes',
  onOpen,
}: {
  count?: number;
  label?: string;
  onOpen: () => void;
}) {
  const hasNotes = count > 0;
  const Icon = hasNotes ? MessageSquareText : MessageSquare;
  const fullLabel = hasNotes ? `${label} (${count})` : label;
  return (
    <button
      type="button"
      title={fullLabel}
      aria-label={fullLabel}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onOpen();
      }}
      className={
        'relative inline-flex items-center text-amp-ink-muted hover:text-amp-ink ' +
        'dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink ' +
        (hasNotes ? 'text-amp-olive dark:text-amp-olive-dark' : '')
      }
    >
      <Icon className="h-4 w-4" aria-hidden />
      {hasNotes && (
        <span
          aria-hidden
          className="absolute -right-1.5 -top-1.5 inline-flex min-w-[14px] items-center justify-center rounded-full bg-amp-olive px-1 text-[10px] font-semibold leading-[14px] text-amp-cream dark:bg-amp-olive-dark"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

/**
 * A destructive dismiss action ("this record is not what the tool tracks"),
 * e.g. "Not an invoice". Always behind a confirm so it is not a one-click
 * accident. The host owns the mutation; this only gates it on confirm.
 */
export function DismissRecordButton({
  label = 'Not an invoice',
  confirmMessage = 'Mark this as not an invoice? It will be hidden from the tool.',
  onConfirm,
  disabled = false,
}: {
  label?: string;
  confirmMessage?: string;
  onConfirm: () => void;
  disabled?: boolean;
}) {
  return (
    <RowIconButton
      icon={Ban}
      label={label}
      disabled={disabled}
      onClick={() => {
        if (window.confirm(confirmMessage)) onConfirm();
      }}
    />
  );
}
